import React, {Fragment, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react'
import {Outlet} from "react-router-dom";
import {RequireAuth} from "context/AuthContext";
import {RequireOperator, useOperator} from "context/OperatorContext";
import {MenuIcon, XIcon} from "@heroicons/react/outline";
import Logo from "components/Logo";
import CurrentUser from "components/CurrentUser";
import SidebarNav from "components/SidebarNav";
import {AppUpdateAlert} from "../components/AppUpdateAlert";
import packageInfo from '../../package.json';

function Authenticated() {
    const {operator} = useOperator()
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

    const operatorLogo = operator?.settings.branding.logo

    return (
        <RequireOperator>
          <RequireAuth>
            <>
              {/* Sliding sidebar for mobile */}
              <Transition.Root show={sidebarOpen} as={Fragment}>
                <Dialog as="div" className="fixed inset-0 flex z-40 md:hidden" onClose={setSidebarOpen}>
                  <Transition.Child
                    as={Fragment}
                    enter="transition-opacity ease-linear duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity ease-linear duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75"/>
                  </Transition.Child>
                  <Transition.Child
                    as={Fragment}
                    enter="transition ease-in-out duration-300 transform"
                    enterFrom="-translate-x-full"
                    enterTo="translate-x-0"
                    leave="transition ease-in-out duration-300 transform"
                    leaveFrom="translate-x-0"
                    leaveTo="-translate-x-full"
                  >
                    {/* Sidebar component */}
                    <div className="relative flex-1 flex flex-col max-w-[17rem] bg-white">
                      <Transition.Child
                        as={Fragment}
                        enter="ease-in-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in-out duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <div className="absolute top-0 right-0 -mr-12 pt-2">
                          <button
                            type="button"
                            className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                            onClick={() => setSidebarOpen(false)}
                          >
                            <span className="sr-only">Close sidebar</span>
                            <XIcon className="h-6 w-6 text-white" aria-hidden="true"/>
                          </button>
                        </div>
                      </Transition.Child>
                      <div className="flex-1 h-0 pb-4 overflow-y-auto scrollbar-thin scrollbar-track-gray-200 scrollbar-thumb-gray-400">
                        <div className='flex items-center justify-center min-h-[7rem]'>
                          <div className="max-w-40">
                            <Logo src={operatorLogo?.original_url} size={Logo.sizes.X_LARGE}/>
                          </div>
                        </div>
                        {/*<div className="flex justify-center px-4">*/}
                        {/*  <Logo src={operatorLogo?.original_url} size={Logo.sizes.X_LARGE}/>*/}
                        {/*</div>*/}

                        {/* The navigation menu itself */}
                        <SidebarNav/>
                      </div>

                      <CurrentUser/>
                      <div className="text-gray-500 text-[0.6875rem] font-medium pt-2 pb-3 mx-4 font-mono">n-gage.io (v{packageInfo.version})</div>
                    </div>
                  </Transition.Child>
                  <div className="flex-shrink-0 w-14">{/* Force sidebar to shrink to fit close icon */}</div>
                </Dialog>
              </Transition.Root>

              {/* Static sidebar for desktop */}
              <div className="hidden md:flex md:flex-col md:fixed md:inset-y-0 z-10">
                {/* Sidebar component */}
                <div className="flex-1 flex flex-col min-h-0 border-r-2 border-gray-200 bg-white">
                  <div className="flex-1 flex flex-col pb-4 w-[17rem] overflow-y-auto scrollbar-thin scrollbar-track-gray-200 scrollbar-thumb-gray-400">
                    <div className='flex items-center justify-center min-h-[7rem]'>
                      <div className="max-w-40">
                        <Logo src={operatorLogo?.original_url} size={Logo.sizes.X_LARGE}/>
                      </div>
                    </div>

                    {/* The navigation menu itself */}
                    <SidebarNav/>
                  </div>

                  <CurrentUser/>
                  <div className="text-gray-500 text-[0.6875rem] font-medium pt-2 pb-3 mx-4 font-mono">n-gage.io (v{packageInfo.version})</div>
                </div>
              </div>

              <div className="h-full md:pl-[17rem] flex flex-col flex-1">

                {/* "Open sidebar" button for mobile */}
                <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-100">
                  <button
                    type="button"
                    className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand"
                    onClick={() => setSidebarOpen(true)}
                  >
                    <span className="sr-only">Open sidebar</span>
                    <MenuIcon className="h-6 w-6" aria-hidden="true"/>
                  </button>
                </div>

                {/* Main content */}
                <main className="h-full">
                  <AppUpdateAlert />
                  <Outlet/>
                </main>

                {/* Shortcuts */}
                {/*<div className="fixed z-10 top-1.5 right-5 md:top-auto md:bottom-5 md:right-8 flex md:flex-col space-x-2 md:space-y-3 md:space-x-0">*/}
                {/*  <RedemptionShortcut/>*/}
                {/*</div>*/}
              </div>
            </>
          </RequireAuth>
        </RequireOperator>
    );
}

export default Authenticated;